// basemapSwapQueue.js — Ticket 03: serialize + guard basemap style swaps against
// `applyMapboxStyle`'s async, unsequenced fetch-and-rebuild.
//
// Problem: `applyMapboxStyle(map, basemapId)` fetches+parses a style JSON and adds tile
// layers when its promise resolves. With no sequencing guard, requesting basemap A then
// quickly B can have A's promise resolve *after* B's (out-of-order network/parse timing),
// so A's tile layers get added last — the user is stuck looking at a stale style. The same
// applies on unmount: an in-flight apply resolving after the map is torn down would try to
// mutate a dead OL Map.
//
// Fix: a tiny serializing queue, keyed per Map instance's swap sequence. Each `request()`:
//   1. Is stamped with the latest-requested basemap id at call time.
//   2. Waits for any in-flight/prior-queued request for the same map to settle first
//      (so applies never race/interleave for a single map).
//   3. Right before actually calling `applyStyle`, re-checks that this request is still the
//      *latest* one requested for this map (and, if given, that the map/component is still
//      current) — if a newer request has since been queued, or the map/component was torn
//      down, this one is skipped entirely (no-op), leaving the newer/no request to apply.
//
// This is a plain, framework-independent module (no React/OL imports) so it's trivially
// unit-testable and reusable across MapEditorModal's two applyMapboxStyle call sites
// (initial mount + basemap-change swap).

export function createBasemapSwapQueue() {
    // One "latest requested id" + one "chain tail promise" per distinct `map` key, so that
    // separate Map instances (e.g. this modal instance vs. a previous one that's mid-teardown)
    // never block or interfere with each other.
    const latestByMap = new Map();
    const chainByMap = new Map();

    function request({ map, basemapId, applyStyle, beforeApply, isMapCurrent, onError }) {
        latestByMap.set(map, basemapId);

        const prior = chainByMap.get(map) || Promise.resolve();
        const runThis = prior.then(async () => {
            // Stale check #1: a newer basemap was requested for this map while we were
            // waiting our turn in the chain — skip entirely, let the newer request apply.
            if (latestByMap.get(map) !== basemapId) return;
            // Stale check #2: the map/component is no longer current (e.g. unmounted).
            if (isMapCurrent && !isMapCurrent()) return;

            if (beforeApply) beforeApply();
            try {
                await applyStyle(map, basemapId);
            } catch (err) {
                if (onError) onError(err);
                // Swallow so the chain keeps processing subsequent requests.
            }
        });

        // Keep the chain alive for subsequent requests even if this one errors internally
        // (errors are already caught above, but guard defensively either way).
        chainByMap.set(map, runThis.catch(() => {}));
        return runThis;
    }

    return { request };
}
